// src/components/layout/AppLayout.jsx
import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';
import websiteTimeTracker from '../../services/websiteTimeTracker';
import { GAMES_ENABLED } from '../../config/featureFlags';

// Party games are dev-only; lazy so production builds don't bundle them.
const PartyGameDrawer = GAMES_ENABLED ? React.lazy(() => import('../game/PartyGameDrawer')) : null;
const Top10Drawer = GAMES_ENABLED ? React.lazy(() => import('../game/top10/Top10Drawer')) : null;
const FishbowlDrawer = GAMES_ENABLED ? React.lazy(() => import('../game/fishbowl/FishbowlDrawer')) : null;
const PsychDrawer = GAMES_ENABLED ? React.lazy(() => import('../game/psych/PsychDrawer')) : null;
const DixitDrawer = GAMES_ENABLED ? React.lazy(() => import('../game/dixit/DixitDrawer')) : null;

const isGrammarPracticePath = (pathname) =>
  pathname.includes('practice/grammar');

const isDashboardPath = (pathname) =>
  pathname.endsWith('/dashboard') || pathname.includes('/dashboard');

const AppLayout = (props) => {
  const navigate = useLocalizedNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isPartyGameOpen, setIsPartyGameOpen] = useState(false);
  const [isTop10Open, setIsTop10Open] = useState(false);
  const [isFishbowlOpen, setIsFishbowlOpen] = useState(false);
  const [isPsychOpen, setIsPsychOpen] = useState(false);
  const [isDixitOpen, setIsDixitOpen] = useState(false);
  const [isGameMenuOpen, setIsGameMenuOpen] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  // The digital simulator and the paper exam (booklet view) both run full screen.
  const isExamSimulatorPage = location.pathname.includes('/exam/simulator')
    || (location.pathname.includes('/exams/') && /\/(simulator|paper)(\/|$)/.test(location.pathname));
  // The paper exam takes the whole window, as the printed booklet view needs:
  // no app header and no page-width column (it has its own toolbar and back).
  const isPaperExamPage = /\/exams\/[^/]+\/paper(\/|$)/.test(location.pathname);

  // Focus mode only belongs on grammar practice; clear it when user navigates elsewhere (e.g. back, sidebar)
  useEffect(() => {
    if (!isGrammarPracticePath(location.pathname)) {
      setFocusMode(false);
    }
  }, [location.pathname]);

  // Record which parts of the paid product this account opened. AppLayout wraps
  // exactly the authenticated routes, so this covers the paid surface and
  // nothing public.
  useEffect(() => {
    websiteTimeTracker.recordPath(location.pathname);
  }, [location.pathname]);

  const handleNavigate = (path) => {
    navigate(path);
    setIsSidebarOpen(false);
  };

  return (
    <div className="flex h-screen bg-white font-sans safe-area-inset">
      {/* Mobile Sidebar Overlay - Smooth Animation like SlideOutPanel */}
      {!isExamSimulatorPage && !focusMode && (
        <>
          {/* Blurred Backdrop - Similar to SlideOutPanel */}
          <div
            className={`fixed inset-0 transition-all duration-500 z-40 lg:hidden ${
              isSidebarOpen 
                ? 'backdrop-blur-md bg-black/30 opacity-100' 
                : 'backdrop-blur-none opacity-0 pointer-events-none'
            }`}
            onClick={() => setIsSidebarOpen(false)}
          />

          {/* Slide Panel - Similar to SlideOutPanel */}
          <div
            className={`fixed top-0 left-0 h-full overflow-hidden bg-white shadow-2xl transition-transform duration-500 ease-out z-50 lg:hidden border-r-[3px] border-black ${
              isSidebarOpen ? 'translate-x-0 w-80 max-w-[85vw]' : '-translate-x-full w-80 max-w-[85vw]'
            }`}
            style={{
              boxShadow: '10px 0 60px rgba(0, 0, 0, 0.3)'
            }}
          >
            <Sidebar
              onNavigate={handleNavigate}
              onLogout={props.onLogout}
              onClose={() => setIsSidebarOpen(false)}
              isMobile={true}
              user={props.user}
              avatarUrl={props.avatarUrl}
              onShareApp={props.onShareApp}
              onInstallApp={props.onInstallApp}
              isInstallable={props.isInstallable}
              isStandalone={props.isStandalone}
              isAdmin={props.isAdmin}
              onOpenPartyGame={() => { setIsPartyGameOpen(true); setIsSidebarOpen(false); }}
              onOpenTop10={() => { setIsTop10Open(true); setIsSidebarOpen(false); }}
              onOpenFishbowl={() => { setIsFishbowlOpen(true); setIsSidebarOpen(false); }}
              onOpenPsych={() => { setIsPsychOpen(true); setIsSidebarOpen(false); }}
              onOpenDixit={() => { setIsDixitOpen(true); setIsSidebarOpen(false); }}
            />
          </div>
        </>
      )}

      {/* Desktop Sidebar - Hidden on mobile */}
      {!isExamSimulatorPage && !focusMode && (
        <div className="hidden lg:block">
          <Sidebar
            onNavigate={handleNavigate}
            onLogout={props.onLogout}
            isMobile={false}
            user={props.user}
            avatarUrl={props.avatarUrl}
            onShareApp={props.onShareApp}
            onInstallApp={props.onInstallApp}
            isInstallable={props.isInstallable}
            isStandalone={props.isStandalone}
            isAdmin={props.isAdmin}
            onOpenPartyGame={() => setIsPartyGameOpen(true)}
            onOpenTop10={() => setIsTop10Open(true)}
            onOpenFishbowl={() => setIsFishbowlOpen(true)}
            onOpenPsych={() => setIsPsychOpen(true)}
            onOpenDixit={() => setIsDixitOpen(true)}
          />
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {!isPaperExamPage && (
          <Header
            {...props}
            onNavigate={handleNavigate}
            onToggleSidebar={() => setIsSidebarOpen(true)}
            isExamSimulatorPage={isExamSimulatorPage}
            focusMode={focusMode}
          />
        )}
        <main
  className="flex-1 overflow-x-hidden overflow-y-auto transition-all duration-500 p-0"
  style={{ backgroundColor: focusMode ? '#ffffff' : isDashboardPath(location.pathname) ? '#F5F5F7' : '#ffffff' }}
>
  <div className={focusMode || isPaperExamPage ? 'h-full' : 'max-w-7xl mx-auto'}>
    <Outlet context={{
      ...props,
      focusMode,
      setFocusMode,
      onNavigate: handleNavigate,
      onOpenPartyGame: () => setIsPartyGameOpen(true),
      onOpenTop10: () => setIsTop10Open(true),
      onOpenFishbowl: () => setIsFishbowlOpen(true),
      onOpenPsych: () => setIsPsychOpen(true),
      onOpenDixit: () => setIsDixitOpen(true),
    }} />
  </div>
</main>
     </div>

      {/* Game FAB + Menu */}
      {GAMES_ENABLED && !isExamSimulatorPage && !focusMode && (
        <>
          {props.isAdmin && (
            <button
              className="party-fab"
              onClick={() => setIsGameMenuOpen((v) => !v)}
              aria-label="Open Game Menu"
            >
              🎲
            </button>
          )}

          {isGameMenuOpen && (
            <>
              <div className="game-menu-backdrop" onClick={() => setIsGameMenuOpen(false)} />
              <div className="game-menu-popup">
                <button
                  className="game-menu-item"
                  onClick={() => { setIsPartyGameOpen(true); setIsGameMenuOpen(false); }}
                >
                  <span className="game-menu-icon">🗣️</span>
                  <div className="game-menu-text">
                    <span className="game-menu-name">Articulate</span>
                    <span className="game-menu-desc">Word guessing relay</span>
                  </div>
                </button>
                <button
                  className="game-menu-item"
                  onClick={() => { setIsTop10Open(true); setIsGameMenuOpen(false); }}
                >
                  <span className="game-menu-icon">🔟</span>
                  <div className="game-menu-text">
                    <span className="game-menu-name">Top 10</span>
                    <span className="game-menu-desc">Cooperative ordering game</span>
                  </div>
                </button>
                <button
                  className="game-menu-item"
                  onClick={() => { setIsFishbowlOpen(true); setIsGameMenuOpen(false); }}
                >
                  <span className="game-menu-icon">🐟</span>
                  <div className="game-menu-text">
                    <span className="game-menu-name">Fishbowl</span>
                    <span className="game-menu-desc">3-round party classic</span>
                  </div>
                </button>
                <button
                  className="game-menu-item"
                  onClick={() => { setIsPsychOpen(true); setIsGameMenuOpen(false); }}
                >
                  <span className="game-menu-icon">🧠</span>
                  <div className="game-menu-text">
                    <span className="game-menu-name">Psych!</span>
                    <span className="game-menu-desc">Bluff with fake answers</span>
                  </div>
                </button>
                <button
                  className="game-menu-item"
                  onClick={() => { setIsDixitOpen(true); setIsGameMenuOpen(false); }}
                >
                  <span className="game-menu-icon">🃏</span>
                  <div className="game-menu-text">
                    <span className="game-menu-name">Dixit</span>
                    <span className="game-menu-desc">Storytelling card game</span>
                  </div>
                </button>
              </div>
            </>
          )}
        </>
      )}

      {/* Game Drawers (dev only) */}
      {GAMES_ENABLED && (
        <React.Suspense fallback={null}>
          <PartyGameDrawer
            isOpen={isPartyGameOpen}
            onClose={() => setIsPartyGameOpen(false)}
          />
          <Top10Drawer
            isOpen={isTop10Open}
            onClose={() => setIsTop10Open(false)}
          />
          <FishbowlDrawer
            isOpen={isFishbowlOpen}
            onClose={() => setIsFishbowlOpen(false)}
          />
          <PsychDrawer
            isOpen={isPsychOpen}
            onClose={() => setIsPsychOpen(false)}
          />
          <DixitDrawer
            isOpen={isDixitOpen}
            onClose={() => setIsDixitOpen(false)}
          />
        </React.Suspense>
      )}

    </div>
  );
};
export default AppLayout;